import { useTranslation } from "react-i18next";

const routeIcons = [
  "ri-shield-check-fill",
  "ri-ship-2-fill",
  "ri-map-pin-2-fill",
  "ri-building-2-fill",
];

const depCountries = [
  { flag: "🇦🇪", name: "UAE (Dubai)", roleKey: "transport.role.transit", volume: "3.4M bbl/day" },
  { flag: "🇸🇦", name: "Saudi Arabia", roleKey: "transport.role.operator", volume: "12.5M bbl/day" },
  { flag: "🇮🇷", name: "Iran", roleKey: "transport.role.forced", volume: "2.1M bbl/day" },
  { flag: "🇰🇼", name: "Kuwait", roleKey: "transport.role.transit", volume: "2.8M bbl/day" },
  { flag: "🇧🇭", name: "Bahrain", roleKey: "transport.role.transit", volume: "0.2M bbl/day" },
];

export default function TransportRoutes() {
  const { t } = useTranslation();

  const routes = [
    { icon: routeIcons[0], title: t("transport.route1.title"), desc: t("transport.route1.desc"), status: t("transport.route1.status") },
    { icon: routeIcons[1], title: t("transport.route2.title"), desc: t("transport.route2.desc"), status: t("transport.route2.status") },
    { icon: routeIcons[2], title: t("transport.route3.title"), desc: t("transport.route3.desc"), status: t("transport.route3.status") },
    { icon: routeIcons[3], title: t("transport.route4.title"), desc: t("transport.route4.desc"), status: t("transport.route4.status") },
  ];

  return (
    <section id="transport" className="bg-[#0b0b0b] py-24 px-6">
      <div className="max-w-7xl mx-auto">

        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="w-8 h-[2px] bg-[#c9a84c]" />
            <span className="text-[#c9a84c] text-xs font-bold tracking-[4px] uppercase">{t("transport.badge")}</span>
            <div className="w-8 h-[2px] bg-[#c9a84c]" />
          </div>
          <h2 className="text-4xl md:text-5xl font-black text-white mb-4">
            {t("transport.title1")} <span className="text-[#c9a84c]">{t("transport.title2")}</span>
          </h2>
          <p className="text-white/60 max-w-2xl mx-auto text-base leading-relaxed">{t("transport.desc")}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-16">

          {/* MAP WITH HORMUZ OVERLAY */}
          <div className="relative rounded-2xl overflow-hidden border border-white/10">
            <img
              src="https://static.readdy.ai/image/5490f3a4ad0e9eb55a0769efce385370/501af67acdfcd04296186e0403b6af4e.png"
              alt="Saudi Arabia major oil and natural gas infrastructure map"
              className="w-full block"
            />
            <svg
              className="absolute inset-0 w-full h-full"
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
              style={{ pointerEvents: "none" }}
            >
              <defs>
                <filter id="hz-glow" x="-100%" y="-100%" width="300%" height="300%">
                  <feGaussianBlur stdDeviation="0.6" result="blur" />
                  <feMerge>
                    <feMergeNode in="blur" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
                <style>{`
                  @keyframes hz-ring {
                    0%   { r: 2.5; opacity: 0.8; }
                    100% { r: 6;   opacity: 0;   }
                  }
                  .hz-r1 { animation: hz-ring 2s ease-out infinite; }
                  .hz-r2 { animation: hz-ring 2s ease-out 1s infinite; }
                `}</style>
              </defs>
              <circle cx="89" cy="42" r="2.5" fill="#ef4444" fillOpacity="0.45" className="hz-r1" />
              <circle cx="89" cy="42" r="2.5" fill="#ef4444" fillOpacity="0.45" className="hz-r2" />
              <ellipse cx="89" cy="42" rx="3.8" ry="2" fill="#ef4444" fillOpacity="0.22" stroke="#ef4444" strokeWidth="0.35" strokeOpacity="0.95" filter="url(#hz-glow)" />
              <line x1="86.5" y1="40.2" x2="91.5" y2="43.8" stroke="#ef4444" strokeWidth="0.8" strokeLinecap="round" filter="url(#hz-glow)" />
              <line x1="91.5" y1="40.2" x2="86.5" y2="43.8" stroke="#ef4444" strokeWidth="0.8" strokeLinecap="round" filter="url(#hz-glow)" />
            </svg>
            <div className="absolute" style={{ top: "36%", right: "4%" }}>
              <div className="bg-red-700/90 border border-red-400/60 text-white text-[9px] font-black px-2 py-1 rounded-md uppercase tracking-wider whitespace-nowrap">
                ⛔ HORMUZ CLOSED
              </div>
            </div>
          </div>

          {/* ROUTE CARDS */}
          <div className="flex flex-col gap-4">
            {routes.map((r) => (
              <div key={r.title} className="bg-white/5 border border-white/10 rounded-xl p-5 flex items-start gap-4 hover:border-[#c9a84c]/40 transition-colors">
                <div className="w-10 h-10 flex items-center justify-center bg-[#c9a84c]/10 rounded-lg shrink-0">
                  <i className={`${r.icon} text-[#c9a84c] text-xl`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <h3 className="text-white font-bold text-sm">{r.title}</h3>
                    <span className="text-[10px] bg-[#c9a84c]/15 text-[#c9a84c] px-2 py-0.5 rounded-full font-semibold whitespace-nowrap shrink-0">{r.status}</span>
                  </div>
                  <p className="text-white/50 text-xs leading-relaxed mb-2">{r.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* DEPENDENT COUNTRIES */}
        <div className="bg-white/5 border border-white/10 rounded-2xl p-8">
          <h3 className="text-white font-black text-xl mb-6 flex items-center gap-2">
            <i className="ri-global-line text-[#c9a84c]" />
            {t("transport.tableTitle")}
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[500px]">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left text-white/40 text-xs uppercase tracking-widest font-semibold pb-3">{t("transport.col.country")}</th>
                  <th className="text-left text-white/40 text-xs uppercase tracking-widest font-semibold pb-3">{t("transport.col.role")}</th>
                  <th className="text-right text-white/40 text-xs uppercase tracking-widest font-semibold pb-3">{t("transport.col.volume")}</th>
                </tr>
              </thead>
              <tbody>
                {depCountries.map((c) => (
                  <tr key={c.name} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                    <td className="py-4 text-white font-semibold flex items-center gap-3 text-sm">
                      <span className="text-2xl">{c.flag}</span>
                      {c.name}
                    </td>
                    <td className="py-4">
                      <span className={`text-xs px-2 py-1 rounded-full font-semibold ${
                        c.roleKey === "transport.role.operator"
                          ? "bg-[#c9a84c]/20 text-[#c9a84c]"
                          : "bg-white/10 text-white/60"
                      }`}>
                        {t(c.roleKey)}
                      </span>
                    </td>
                    <td className="py-4 text-right text-[#c9a84c] font-bold text-sm">{c.volume}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </section>
  );
}
